import { type ComponentPropsWithoutRef } from 'react'
import { Check, Loader2, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

/**
 * Corner badge marking a staged action that failed to execute.
 *
 * Deliberately the mirror of `ActionSuccessBadge`, down to the size and shadow:
 * an outcome landing keeps the flat list-row shape instead of inflating into a
 * card, so the badge is the only thing that changes at rest. The message and
 * its Retry live in the hover card.
 */
export function ActionErrorBadge() {
  return (
    <div className="bg-danger flex size-3 shrink-0 items-center justify-center rounded-full shadow-sm">
      <X className="text-on-danger size-2" strokeWidth={3} />
    </div>
  )
}

/**
 * Corner badge marking a staged action that executed — the failure badge above
 * with the glyph and the tint swapped. `bg-success` rather than a raw green so
 * it agrees with the expiry progress bar underneath the same row.
 */
export function ActionSuccessBadge() {
  return (
    <div className="bg-success flex size-3 shrink-0 items-center justify-center rounded-full shadow-sm">
      <Check className="text-on-success size-2" strokeWidth={3} />
    </div>
  )
}

export function ActionSendingBadge() {
  return (
    <div className="bg-orange-9 flex size-3 shrink-0 items-center justify-center rounded-full shadow-sm">
      <Loader2 className="text-on-dark size-2 animate-spin" strokeWidth={3} />
    </div>
  )
}

/**
 * The in-cell failure line for surfaces that still render the expanded cell
 * (the Actions view, and undo errors on an already-executed action).
 *
 * Matches the Action View's error treatment — tinted text on a soft fill, no
 * icon, no border — so the same failure reads the same everywhere.
 */
export function ActionErrorNote({ className, children, ...props }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={twMerge(
        'bg-danger-subtle text-danger-subtle corner-smooth type-ui-xs mt-2 flex items-center gap-2 rounded-lg px-3 py-2',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}
